import React, { useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Paper,
  Chip,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InventoryIcon from "@mui/icons-material/Inventory";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import PlaceIcon from "@mui/icons-material/Place";
import PaletteIcon from "@mui/icons-material/Palette";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAdminNav } from "../../layouts/AdminNavContext";

import useOrdersData from "./BookingPage/hooks/useOrdersData";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { isDelivery, normalizeLocation } from "../../utils/fulfillment";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

const Section = ({ title, children, sx }) => (
  <Box sx={{ mb: 3, ...sx }}>
    <Typography variant="h6" fontWeight={700} sx={{ mb: 1.25 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const StatCard = ({ icon, label, value, onClick }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { bgcolor: "action.hover" } : undefined,
    }}
    onClick={onClick}>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ color: "text.secondary" }}>{icon}</Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
    <Typography variant="h4" fontWeight={800}>
      {value}
    </Typography>
  </Paper>
);

const QuickTile = ({ icon, label, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 2,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      cursor: "pointer",
      "&:hover": { bgcolor: "action.hover" },
    }}>
    <Box sx={{ color: "text.secondary" }}>{icon}</Box>
    <Typography fontWeight={600}>{label}</Typography>
  </Paper>
);

// Compact, mobile-safe upcoming row (no secondaryAction overlap)
const UpcomingRow = ({ order, onClick }) => {
  const c = order.customer || {};
  const f = order.fulfillment || {};
  const m = order.meta || {};
  const del = isDelivery(f);
  const tz = "America/St_Johns";
  const time = f.time || dayjs.tz(f.date?.$date || f.date, tz).format("h:mm A");

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.25,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
        cursor: "pointer",
        borderTop: "1px solid",
        borderColor: "divider",
      }}>
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          flexWrap="wrap">
          <Box sx={{ color: "text.secondary", display: "inline-flex" }}>
            {del ? (
              <LocalShippingIcon fontSize="small" />
            ) : (
              <ShoppingBagIcon fontSize="small" />
            )}
          </Box>
          <Typography
            fontWeight={700}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
            {c.firstName} {c.lastName}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.25 }}>
          {time} • {del ? "Delivery" : normalizeLocation(f)}
        </Typography>
      </Stack>

      <Chip
        size="small"
        color={m.status === "in-progress" ? "warning" : "default"}
        label={m.status}
        sx={{ alignSelf: "flex-start", flexShrink: 0 }}
      />
    </Box>
  );
};

const AdminHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openNav } = useAdminNav();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Today’s orders for overview
  const todayStr = dayjs().format("YYYY-MM-DD");
  const { orders, refresh, lastRefreshedAt } = useOrdersData({
    showLegacy: false,
    dateOn: "pickup",
    startDate: todayStr,
    endDate: todayStr,
  });

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return;
      const k = e.key.toLowerCase();
      if (k === "m") openNav?.();
      if (k === "r") refresh();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openNav, refresh]);

  const { pickupsToday, deliveriesToday } = useMemo(() => {
    let p = 0,
      d = 0;
    for (const ord of orders) isDelivery(ord.fulfillment) ? d++ : p++;
    return { pickupsToday: p, deliveriesToday: d };
  }, [orders]);

  const upcoming = useMemo(() => {
    const tz = "America/St_Johns";
    return [...orders]
      .sort((a, b) => {
        const da = dayjs
          .tz(a.fulfillment?.date?.$date || a.fulfillment?.date, tz)
          .valueOf();
        const db = dayjs
          .tz(b.fulfillment?.date?.$date || b.fulfillment?.date, tz)
          .valueOf();
        return da - db;
      })
      .slice(0, 8);
  }, [orders]);

  const role = user?.employee?.role ?? user?.role;
  const canSeeStaffContent = ["admin", "manager", "eventsManager", "staff"];
  const canSeeEvents = ["admin", "eventsManager"];

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Typography variant="h5" fontWeight={800}>
            Admin Dashboard{user?.firstName ? ` — Hi, ${user.firstName}` : ""}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refresh (R)">
            <Button size="small" startIcon={<RefreshIcon />} onClick={refresh}>
              Refresh
            </Button>
          </Tooltip>
          {lastRefreshedAt && (
            <Typography variant="caption" color="text.secondary">
              Updated {dayjs(lastRefreshedAt).fromNow()}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* KPIs (no Overdue) */}
      <Section title="Today">
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={3} sx={{ mb: 5 }}>
            <StatCard
              icon={<StorefrontIcon />}
              label="Pickups"
              value={pickupsToday}
              onClick={() => navigate("/admin/booking")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ mb: 5 }}>
            <StatCard
              icon={<LocalShippingIcon />}
              label="Deliveries"
              value={deliveriesToday}
              onClick={() => navigate("/admin/booking")}
            />
          </Grid>
        </Grid>
      </Section>

      {/* Upcoming list (mobile-safe rows) */}
      <Section title="Upcoming (today)">
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          {upcoming.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No orders today.
              </Typography>
            </Box>
          ) : (
            <>
              {/* first row has its own border via Paper; others via borderTop in UpcomingRow */}
              <Box sx={{ px: 2, py: 1.25 }}>
                <Typography variant="body2" color="text.secondary">
                  {upcoming.length} order{upcoming.length > 1 ? "s" : ""}
                </Typography>
              </Box>
              {upcoming.map((o, i) => (
                <UpcomingRow
                  key={o._id || i}
                  order={o}
                  onClick={() => navigate("/admin/booking")}
                />
              ))}
            </>
          )}
        </Paper>
      </Section>

      {/* Quick navigation (removed Business Overview) */}
      {canSeeStaffContent.includes(role) && (
        <Section title="Quick navigation">
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={4}>
              <QuickTile
                icon={<ShoppingBagIcon />}
                label="Orders"
                onClick={() => navigate("/admin/booking")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickTile
                icon={<CardGiftcardIcon />}
                label="Loyalty Members"
                onClick={() => navigate("/admin/loyalty-program")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickTile
                icon={<InventoryIcon />}
                label="Supply Items"
                onClick={() => navigate("/admin/supply-items")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickTile
                icon={<ReceiptLongIcon />}
                label="Submit Grocery List"
                onClick={() => navigate("/admin/shopping-list")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <QuickTile
                icon={<PlaceIcon />}
                label="Driver Checklist"
                onClick={() => navigate("/admin/driver-checklist")}
              />
            </Grid>
            {canSeeEvents.includes(role) && (
              <Grid item xs={12} sm={6} md={4}>
                <QuickTile
                  icon={<PaletteIcon />}
                  label="Decorating Classes"
                  onClick={() => navigate("/admin/decorating-classes")}
                />
              </Grid>
            )}
          </Grid>
        </Section>
      )}
    </Box>
  );
};

export default AdminHome;
